import { CurrencyCode } from '@mondough/money'

import { useCopy } from './'
import {
  CardinalForms,
  Components,
  CopyArgs,
  CopyKey,
  CopyWithComponent,
  CopyWithVariable,
  OrdinalForms,
} from './types'

export const Copy = {
  Simple,
  Plural,
  Money,
}

function requiresInterpolation(
  props: SimpleCopyProps | InterpolatedCopyProps,
): props is InterpolatedCopyProps {
  return 'variables' in props || 'components' in props
}

function Simple(props: SimpleCopyProps | InterpolatedCopyProps) {
  const c = useCopy()
  const copy = requiresInterpolation(props)
    ? c({
        key: props.id,
        message: props.fallback,
        variables: props.variables,
        components: props.components,
      })
    : c({ key: props.id, message: props.fallback })

  return <>{copy}</>
}

function Plural({
  id,
  fallbacks,
  pluralType,
  variable,
  components,
}: PluralCopyProps) {
  const c = useCopy()
  const pluralString = c({
    key: id,
    message: fallbacks,
    plural: c.plural(variable.name, variable.value, pluralType),
    components: components,
  })

  return <>{pluralString}</>
}

function Money(props: MoneyProps) {
  const c = useCopy()
  const moneyString = c.money(props.value, props.currencyCode)
  return <>{moneyString}</>
}

type SimpleCopyProps<Message extends string = string> = {
  id: CopyArgs<Message>['key']
  fallback: CopyArgs<Message>['message']
}

type InterpolatedCopyProps<
  Message extends CopyWithVariable | CopyWithComponent =
    | CopyWithVariable
    | CopyWithComponent,
> = {
  id: CopyArgs<Message>['key']
  fallback: CopyArgs<Message>['message']
  variables: CopyArgs<Message>['variables']
  components: CopyArgs<Message>['components']
}

type MoneyProps = {
  value: number
  currencyCode: CurrencyCode
}

type PluralCopyProps = {
  id: CopyKey
  pluralType: 'cardinal' | 'ordinal'
  variable: { name: string; value: number }
  fallbacks: CardinalForms | OrdinalForms
  components?: Components<CopyWithComponent>
}
